import { hc, type InferResponseType } from "hono/client";
import { baseUrl } from "../../libs/client/baseUrl";
import type { MypageApiRoutes } from "../../routes/_apis/mypage";

/**
 * セキュア (https) のときは __Host- プレフィクス、localhost dev では通常名 (mp_csrf)。
 * サーバ側 (csrf.ts) と一致させる。
 */
const csrfCookieName = (): string =>
  globalThis.location?.protocol === "https:" ? "__Host-mp_csrf" : "mp_csrf";

const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match?.[1];
};

/**
 * 全リクエスト共通の fetch ラッパ:
 * - credentials: "include" でセッション Cookie を載せる
 * - X-CSRF-Token ヘッダを CSRF Cookie から自動付与 (state-changing 系で必要)
 *
 * 注意: CSRF Cookie が無いとヘッダは付かない (`if (csrfToken)`)。サーバ側 (`requireCsrf`) は
 * Cookie/ヘッダ両方必須なので、Cookie 不在でリクエストすると 403 が返って UI が混乱する。
 * **`/me` を必ず最初に踏んで Cookie を確立する**前提でフロントを設計している (`useMe` 経由)。
 * SPA 初期化前に直接 state-changing API を叩くデバッグ用 UI 等を追加するときは、先に `/me`
 * を await すること。
 */
/**
 * 全リクエスト共通の fetch ラッパ。`apiClient` 内部で hono client が使う他、multipart
 * アップロードのように hono client から直接投げにくい経路でも同じ CSRF / cookie
 * ロジックを使い回せるよう export する。
 */
export const customFetch: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  const csrfToken = readCookie(csrfCookieName());
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  return fetch(input, { ...init, credentials: "include", headers });
};

export const apiClient = hc<MypageApiRoutes>(baseUrl("/mypage/api/"), { fetch: customFetch });

export const authApi = apiClient.auth;

/** GET /auth/me の成功レスポンスから user を取り出した型 */
export type Me = NonNullable<InferResponseType<typeof authApi.me.$get, 200>["user"]>;
